import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RecommendationEngine = ({ assessmentData, onScheduleCall }) => {
  const generateRecommendations = () => {
    const recommendations = [];
    
    // Velocity-based recommendations
    if (assessmentData.velocity <= 2) {
      recommendations.push({
        priority: 'high',
        service: 'Velocity Acceleration Program',
        timeline: '8-12 weeks',
        impact: 'Increase delivery speed by 200-300%',
        description: 'Comprehensive velocity transformation focusing on process optimization, technical debt reduction, and team empowerment.',
        keyActions: [
          'Sprint velocity analysis and optimization',
          'Technical debt prioritization framework',
          'Automated testing and CI/CD implementation',
          'Team ownership culture development'
        ]
      });
    }

    // Technical debt recommendations
    if (assessmentData.technical_debt && assessmentData.technical_debt.length > 2) {
      recommendations.push({
        priority: 'high',
        service: 'Technical Debt Remediation',
        timeline: '12-16 weeks',
        impact: 'Reduce maintenance overhead by 40-60%',
        description: 'Systematic approach to identifying, prioritizing, and resolving technical debt while maintaining feature delivery.',
        keyActions: [
          'Technical debt audit and categorization',
          'Modernization roadmap development',
          'Incremental refactoring strategy',
          'Code quality standards implementation'
        ]
      });
    }

    // Ownership culture recommendations
    if (assessmentData.ownership_culture === 'reactive' || assessmentData.ownership_culture === 'process_driven') {
      recommendations.push({
        priority: 'medium',
        service: 'Ownership Culture Transformation',
        timeline: '6-10 weeks',
        impact: 'Improve team autonomy and accountability by 150%',
        description: 'Cultural transformation program that shifts teams from reactive to proactive ownership mentality.',
        keyActions: [
          'Ownership assessment and gap analysis',
          'Accountability framework implementation',
          'Decision-making authority redistribution',
          'Continuous improvement culture development'
        ]
      });
    }

    // Scalability recommendations
    if (assessmentData.scalability === 'struggling' || assessmentData.scalability === 'limited') {
      recommendations.push({
        priority: 'high',
        service: 'Architectural Modernization',
        timeline: '16-24 weeks',
        impact: 'Enable 10x scalability with improved reliability',
        description: 'Complete architectural assessment and modernization to support rapid growth and improved system reliability.',
        keyActions: [
          'Architecture assessment and future-state design',
          'Microservices migration strategy',
          'Cloud-native infrastructure implementation',
          'Monitoring and observability enhancement'
        ]
      });
    }

    return recommendations.sort((a, b) => {
      const priorityOrder = { high: 3, medium: 2, low: 1 };
      return priorityOrder[b.priority] - priorityOrder[a.priority];
    });
  };

  const recommendations = generateRecommendations();

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-error bg-error/10 border-error/20';
      case 'medium': return 'text-warning bg-warning/10 border-warning/20';
      case 'low': return 'text-success bg-success/10 border-success/20';
      default: return 'text-text-secondary bg-muted border-border';
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'high': return 'AlertTriangle';
      case 'medium': return 'Clock';
      case 'low': return 'CheckCircle';
      default: return 'Info';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-accent/10 rounded-full mb-4">
          <Icon name="Target" size={32} color="var(--color-accent)" />
        </div>
        <h2 className="text-2xl font-bold text-text-primary mb-2">
          Your Personalized Transformation Roadmap
        </h2>
        <p className="text-text-secondary max-w-2xl mx-auto">
          Based on your assessment, we've identified the most impactful areas for improvement. 
          Here's your customized approach to engineering excellence.
        </p>
      </div>

      {/* Recommendations */}
      <div className="space-y-6">
        {recommendations.map((rec, index) => (
          <div key={index} className="bg-card rounded-lg border border-border p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-start space-x-4">
                <div className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getPriorityColor(rec.priority)}`}>
                  <Icon name={getPriorityIcon(rec.priority)} size={12} className="mr-1" />
                  {rec.priority.toUpperCase()} PRIORITY
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-text-primary mb-2">
                    {rec.service}
                  </h3>
                  <p className="text-text-secondary mb-3">
                    {rec.description}
                  </p>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <Icon name="Clock" size={16} color="var(--color-accent)" />
                  <div>
                    <span className="text-sm font-medium text-text-primary">Timeline</span>
                    <p className="text-sm text-text-secondary">{rec.timeline}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Icon name="TrendingUp" size={16} color="var(--color-success)" />
                  <div>
                    <span className="text-sm font-medium text-text-primary">Expected Impact</span>
                    <p className="text-sm text-text-secondary">{rec.impact}</p>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-medium text-text-primary mb-3">Key Actions</h4>
                <ul className="space-y-2">
                  {rec.keyActions.map((action, actionIndex) => (
                    <li key={actionIndex} className="flex items-start space-x-2">
                      <Icon name="Check" size={14} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-text-secondary">{action}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-border">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <Icon name="Users" size={16} color="var(--color-text-secondary)" />
                  <span className="text-sm text-text-secondary">Dedicated Team</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="Shield" size={16} color="var(--color-text-secondary)" />
                  <span className="text-sm text-text-secondary">Enterprise Security</span>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                iconName="ArrowRight"
                iconPosition="right"
                onClick={() => onScheduleCall(rec.service)}
              >
                Discuss This Solution
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Next Steps */}
      <div className="bg-accent/5 rounded-lg border border-accent/20 p-6">
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0">
            <Icon name="Lightbulb" size={24} color="var(--color-accent)" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-text-primary mb-2">
              Ready to Transform Your Engineering Organization?
            </h3>
            <p className="text-text-secondary mb-4">
              Schedule a discovery call with one of our principal engineers to discuss your specific challenges 
              and create a detailed transformation plan tailored to your organization.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                variant="default"
                className="bg-accent hover:bg-accent/90"
                iconName="Calendar"
                iconPosition="left"
                onClick={() => onScheduleCall('comprehensive')}
              >
                Schedule Discovery Call
              </Button>
              <Button
                variant="outline"
                iconName="Download"
                iconPosition="left"
              >
                Download Assessment Report
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecommendationEngine;